import axios, { AxiosError, AxiosInstance } from 'axios';

/**
 * API client for Invoice Parse v2 backend.
 *
 * - Same-origin: the SPA is at app.invoice-parse.com, API at
 *   app.invoice-parse.com/api/*, routed by Front Door to the Django App
 *   Service. No CORS needed.
 * - Authentication is via Django session cookies (set during login at
 *   invoice-parse.com/login, scoped to .invoice-parse.com so both
 *   subdomains share them).
 * - CSRF: every mutating request (POST/PUT/PATCH/DELETE) sends the
 *   X-CSRFToken header, read from the csrftoken cookie that Django sets.
 *
 * See InvoiceParse_API_v2_Conventions.docx § 3 (Authentication).
 */

// Base URL comes from Vite env var set at build time.
// - Local dev: '/api' (Vite proxies to http://127.0.0.1:8000)
// - Staging build: '/api' (Front Door routes /api/* to staging Django)
// - Production build: '/api' (Front Door routes /api/* to production Django)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

function getCsrfToken(): string | null {
  const match = document.cookie.match(/(^|;\s*)csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Derives the correct SSR login URL based on active domain.
 * - In Production (app.invoice-parse.com), Django login is at https://invoice-parse.com/login/?next=...
 * - In Staging (app-staging.invoice-parse.com) or local dev, Azure Front Door routes /login/ directly.
 */
export function getLoginUrl(): string {
  if (typeof window === 'undefined') return '/login/';
  const hostname = window.location.hostname;
  const fullUrl = window.location.href;

  if (hostname === 'app.invoice-parse.com') {
    return `https://invoice-parse.com/login/?next=${encodeURIComponent(fullUrl)}`;
  }

  const next = encodeURIComponent(window.location.pathname + window.location.search);
  return `/login/?next=${next}`;
}

export function getLogoutUrl(): string {
  if (typeof window === 'undefined') return '/logout/';
  const hostname = window.location.hostname;

  if (hostname === 'app.invoice-parse.com') {
    return 'https://invoice-parse.com/logout/';
  }

  return '/logout/';
}

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true, // send session cookie
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    timeout: 30_000,
  });

  // Attach CSRF token on every mutating request
  client.interceptors.request.use((config) => {
    const method = (config.method ?? 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      const token = getCsrfToken();
      if (token) {
        config.headers['X-CSRFToken'] = token;
      }
    }
    return config;
  });

  // Handle 401 by redirecting to SSR login
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        // Don't redirect if we're in a test environment or already at login.
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = getLoginUrl();
        }
      }
      return Promise.reject(error);
    },
  );

  return client;
}

export const api = createApiClient();

/**
 * Ensures CSRF cookie is initialized before mutations.
 * Calls GET /api/v2/auth/csrf/ which returns { csrfToken: string }.
 */
export async function bootstrapCsrf(): Promise<string | null> {
  try {
    const res = await api.get<{ csrfToken?: string }>('/v2/auth/csrf/');
    return res.data?.csrfToken ?? getCsrfToken();
  } catch {
    return getCsrfToken();
  }
}

// Uniform error shape per v2 conventions:
//   { error: { code, message, details } }
export interface ApiError {
  error: {
    code: string;
    message: string;
    details: Record<string, unknown> | null;
  };
}

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ApiError).error?.code === 'string'
  );
}

export function extractApiErrorMessage(err: unknown): string {
  if (!err) return 'An unexpected error occurred.';
  if (typeof err === 'string') return err;

  const axiosErr = err as {
    response?: {
      data?: {
        error?: { message?: string; code?: string; details?: Record<string, unknown> | string };
        detail?: string;
        message?: string;
        details?: Record<string, unknown> | string;
        [key: string]: unknown;
      };
      status?: number;
    };
    message?: string;
  };

  const data = axiosErr.response?.data;
  if (data) {
    if (data.error?.message) {
      const detailsStr = data.error.details
        ? ` (${typeof data.error.details === 'object' ? JSON.stringify(data.error.details) : data.error.details})`
        : '';
      return `${data.error.message}${detailsStr}`;
    }
    if (typeof data.error === 'string') return data.error;
    if (data.detail) return String(data.detail);
    if (data.message) return String(data.message);
    if (data.details) {
      return typeof data.details === 'object' ? JSON.stringify(data.details) : String(data.details);
    }
    // If it's a Django ValidationError dictionary, e.g. { "VendorName": ["Invalid vendor"] }
    const entries = Object.entries(data).filter(([k]) => k !== 'error');
    if (entries.length > 0) {
      return entries
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join('; ');
    }
  }

  if (axiosErr.message) return axiosErr.message;
  return 'Failed to save corrections.';
}

